import { FC, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  IPriceRateSearchIdStrip,
  slicePriceRateStripItems,
} from "./rich-logs.id-strips";

interface IRichLogsIdStripRowProps {
  strips: IPriceRateSearchIdStrip[];
  searchText?: string;
}

const isIdHighlighted = (
  id: number,
  searchText: string | undefined,
): boolean => {
  if (!searchText?.trim()) {
    return false;
  }
  const terms = searchText
    .split(",")
    .map(term => term.trim())
    .filter(Boolean);
  const idText = String(id);
  return terms.some(term => idText.includes(term));
};

export const RichLogsIdStripRow: FC<IRichLogsIdStripRowProps> = ({
  strips,
  searchText,
}) => {
  const { t } = useTranslation();
  const [expandedByKind, setExpandedByKind] = useState<
    Partial<Record<IPriceRateSearchIdStrip["kind"], boolean>>
  >({});

  if (!strips.length) {
    return null;
  }

  return (
    <div className="log__id-strips">
      {strips.map(strip => {
        const expanded = Boolean(expandedByKind[strip.kind]);
        const { visibleItems, hiddenCount } = slicePriceRateStripItems({
          items: strip.items,
          expanded,
        });

        if (strip.kind === "filteredOut" && !strip.items.length) {
          return null;
        }

        const resolveStripLabel = (): string => {
          if (strip.kind === "remaining") {
            return t("ride.priceRateSearchRemaining");
          }
          if (strip.reason) {
            return t("ride.priceRateSearchFilteredOutBy", {
              reason: strip.reason,
            });
          }
          return t("ride.priceRateSearchFilteredOut");
        };
        const stripLabel = resolveStripLabel();

        return (
          <div
            key={strip.kind}
            className={`log__id-strip log__id-strip--${
              strip.kind === "remaining" ? "remaining" : "filtered-out"
            }`}
          >
            <span className="log__id-strip-label">
              {stripLabel}
              {` (${strip.items.length})`}
            </span>
            {strip.items.length ? (
              <span className="log__id-strip-ids">
                {visibleItems.map(item => (
                  <button
                    key={`${strip.kind}-${item.id}`}
                    data-id={item.id}
                    data-entity={strip.entity}
                    title={`#${item.id}`}
                    type="button"
                    className={
                      isIdHighlighted(item.id, searchText)
                        ? "log__id-chip log-highlight"
                        : "log__id-chip"
                    }
                  >
                    {item.id}
                  </button>
                ))}
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    className="log__id-strip-more"
                    onClick={event => {
                      event.stopPropagation();
                      setExpandedByKind(current => ({
                        ...current,
                        [strip.kind]: true,
                      }));
                    }}
                  >
                    {t("ride.priceRateSearchShowMoreIds", {
                      defaultValue: "+{{count}} more",
                      count: hiddenCount,
                    })}
                  </button>
                )}
              </span>
            ) : (
              <span className="log__id-strip-empty">
                {t("ride.priceRateSearchNoIds", { defaultValue: "none" })}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};
